import {
  colors,
  ImpButton,
  ImpDivider,
  ImpSpacer,
  ImpText,
} from '@imprint-payments/imprint-ui';
import { ReactElement, useEffect } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { useLogEventOnce } from '../../analytics/analyticsUtil';
import { useImpState } from '../../app/hooks';
import { ANALYTICS } from '../../firebase/firebase';
import type { Address, Dob } from '../../types';

interface InfoFieldsProps {
  nextStep: () => void;
  editInfo: () => void;
}

export default function InfoFields({ nextStep, editInfo }: InfoFieldsProps) {
  const { consumerInfo, language } = useImpState();
  const profile = consumerInfo.consumerProfile;
  const logEventOnce = useLogEventOnce();

  useEffect(() => {
    logEventOnce(EVENT.PERSONAL_INFO_VIEWED, {
      context: 'prefilled',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!profile.ssn9) {
    throw new Error('SSN is required for this screen');
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: '368px',
      }}
    >
      <ImpSpacer height="xl" />
      <ImpText typography={'headline3'}>{language.personalInfo}</ImpText>
      <ImpSpacer height="xs" />
      <ImpText typography={'body2'} color={colors.content.onSurfaceVariant}>
        {language.foundInfo.replace('{fname}', profile.name.firstName)}
      </ImpText>
      <ImpSpacer height="xl" />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor: colors.surface.surfaceMid,
          padding: '0 16px',
          boxSizing: 'border-box',
          borderRadius: '16px',
          textAlign: 'left',
        }}
      >
        <InfoField
          label={language.legalName}
          content={`${profile.name.firstName} ${profile.name.lastName}`}
        />
        <ImpDivider />
        <InfoField
          label={language.personalEmailAddress}
          content={`${profile.email}`}
        />
        <ImpDivider />
        <InfoField
          label={language.birthday}
          content={formatDisplayDob(profile.dob)}
        />
        <ImpDivider />
        <InfoField
          label={language.homeAddress}
          content={<Address addr={profile.address} />}
        />
        <ImpDivider />

        <ImpSpacer height="l" />
        <ImpButton
          variant={'secondary'}
          buttonStyle={'branded'}
          onClick={() => {
            ANALYTICS.logEvent(EVENT.PERSONAL_INFO_EDIT_INFO_CLICKED);
            editInfo();
          }}
          size="fill"
        >
          {language.editInfo}
        </ImpButton>
        <ImpSpacer height="l" />
      </div>

      <ImpSpacer height="xl" />
      <ImpButton
        variant={'primary'}
        buttonStyle={'branded'}
        onClick={() => {
          ANALYTICS.logEvent(EVENT.PERSONAL_INFO_CONTINUE_CLICKED, {
            context: 'prefilled',
          });
          nextStep();
        }}
        size="fill"
      >
        {language.continueBtn}
      </ImpButton>
      <ImpSpacer height="xl" />
    </div>
  );
}

function InfoField(props: { label: string; content: ReactElement | string }) {
  return (
    <>
      <ImpSpacer height="l" />
      <ImpText typography={'body3'} color={colors.content.onSurfaceVariant}>
        {props.label}
      </ImpText>
      <ImpText typography={'body1'} color={colors.content.onSurface}>
        {props.content}
      </ImpText>
      <ImpSpacer height="l" />
    </>
  );
}

/**
 * Format a date of birth to a string of the form "MM/DD/YYYY"
 * @param dob the date of birth object
 * @returns a string of the form "MM / DD / YYYY"
 */
function formatDisplayDob(dob: Dob) {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });
  return `${formatter.format(dob.month)} / ${formatter.format(dob.day)} / ${
    dob.year
  }`;
}

/**
 * Component to render a full address from a [GetPrefillDataResponse.address]
 * @param address the address to render
 * @returns a JSX element containing the formatted address
 */

function Address({ addr }: { addr: Address }) {
  return (
    <>
      {addr.streetLine1} {addr.streetLine2} <br />
      {addr.city}, {addr.state} {addr.zipCode}
    </>
  );
}
